import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowRight, CheckCircle, MapPin, Star,
  Music, Camera, Utensils, Palette, Briefcase, DollarSign
} from 'lucide-react';
import Footer from '../components/Footer';

const EXAMPLE_VENDORS = [
  { name: 'Rivera Audio & DJ', category: 'DJ / Audio', location: 'Austin, TX', price: '$300', icon: Music, color: 'text-purple-600', bg: 'bg-purple-100' },
  { name: 'Capture Studio', category: 'Photography', location: 'Miami, FL', price: '$800', icon: Camera, color: 'text-blue-600', bg: 'bg-blue-100' },
  { name: 'Flourish Decor Co.', category: 'Event Decor', location: 'Denver, CO', price: '$250', icon: Palette, color: 'text-rose-600', bg: 'bg-rose-100' },
  { name: 'Mesa Catering', category: 'Catering', location: 'Phoenix, AZ', price: '$1,000', icon: Utensils, color: 'text-amber-600', bg: 'bg-amber-100' },
];

export default function VendorLandingPage() {
  const navigate = useNavigate();

  const handleJoin = () => {
    navigate('/auth?mode=signup&role=vendor');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-emerald-50/30">
      {/* Header */}
      <header className="px-6 pt-6 pb-2 flex items-center justify-between max-w-lg mx-auto">
        <div className="flex items-center gap-1.5 cursor-pointer" onClick={() => navigate('/')}>
          <img src="/celeyapp-icon-green.png" alt="Celey" className="h-6 w-6" />
          <span className="text-xl font-bold text-gray-900 tracking-tight">Celey</span>
        </div>
        <button
          onClick={() => navigate('/auth?mode=login')}
          className="text-sm font-medium text-gray-600 hover:text-gray-900"
          data-testid="vendor-landing-login-btn"
        >
          Log In
        </button>
      </header>

      <main className="max-w-lg mx-auto px-6 pb-20">
        {/* Hero */}
        <section className="pt-12 pb-6 text-center" data-testid="vendor-landing-hero">
          <h1 className="text-4xl font-bold text-gray-900 leading-tight tracking-tight">
            Get Booked.<br />Stay Organized.<br />Get Paid.
          </h1>
          <p className="mt-4 text-gray-500 text-base">
            Without juggling texts, spreadsheets, or chasing down payments.
          </p>
          <p className="mt-2 text-sm text-emerald-700 font-medium">
            No website? No problem — your CELEY profile becomes your business.
          </p>

          <Button
            onClick={handleJoin}
            fullWidth="true"
            className="mt-8 w-full h-auto min-h-[56px] px-4 sm:px-6 py-4 sm:py-6 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-sm sm:text-base font-semibold shadow-sm whitespace-normal text-center leading-snug"
            data-testid="vendor-landing-join-btn"
          >
            <span className="inline-flex items-center justify-center flex-wrap gap-x-2 gap-y-0">
              <span>Start getting booked — build your profile</span>
              <ArrowRight className="w-4 h-4 shrink-0" />
            </span>
          </Button>

          {/* Trust line */}
          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-400">
            <span>Free to join</span>
            <span className="text-[10px] text-gray-300 mx-1">&bull;</span>
            <span>No monthly fee</span>
            <span className="text-[10px] text-gray-300 mx-1">&bull;</span>
            <span>Set your own pricing</span>
          </div>
        </section>

        {/* Value Strip */}
        <section className="py-6" data-testid="vendor-landing-value-strip">
          <h2 className="text-lg font-bold text-gray-900 text-center mb-5">Why Providers Use CELEY</h2>
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: Briefcase, text: 'Run your business in one place' },
              { icon: DollarSign, text: 'Get paid without chasing down payments' },
              { icon: Star, text: 'Build reviews from day one' },
              { icon: MapPin, text: 'Share a professional profile' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="flex items-start gap-2.5 p-3 bg-white rounded-xl border border-gray-100">
                  <Icon className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span className="text-xs text-gray-700 leading-snug">{item.text}</span>
                </div>
              );
            })}
          </div>
        </section>

        <div className="border-t border-gray-100 my-6" />

        {/* Differentiator */}
        <section className="pb-8" data-testid="vendor-landing-differentiator">
          <h2 className="text-lg font-bold text-gray-900">Don't Start From Scratch</h2>
          <p className="mt-2 text-sm text-gray-600 leading-relaxed">
            Bring your existing clients to CELEY, collect reviews, and build your profile from day one.
          </p>
        </section>

        <div className="border-t border-gray-100 my-6" />

        {/* How It Works */}
        <section className="pb-8" data-testid="vendor-landing-how">
          <h2 className="text-xl font-bold text-gray-900 text-center mb-6">How It Works</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-5 sm:gap-2 items-start">
            {[
              { num: '1', label: 'Receive requests' },
              { num: '2', label: 'Send proposals' },
              { num: '3', label: 'Get booked' },
              { num: '4', label: 'Get paid' },
            ].map((step) => (
              <div key={step.num} className="text-center">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-2">
                  <span className="text-xs font-bold text-emerald-700">{step.num}</span>
                </div>
                <p className="text-xs font-medium text-gray-700 leading-tight">{step.label}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="border-t border-gray-100 my-6" />

        {/* Example Profiles */}
        <section className="pb-8" data-testid="vendor-landing-examples">
          <h2 className="text-lg font-bold text-gray-900 text-center mb-2">What your profile could look like</h2>
          <p className="text-xs text-gray-400 text-center mb-6">See how your profile will look</p>

          <div className="grid grid-cols-2 gap-3">
            {EXAMPLE_VENDORS.map(vendor => {
              const Icon = vendor.icon;
              return (
                <div key={vendor.name} className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm" data-testid="example-vendor-card">
                  <div className={`w-10 h-10 rounded-xl ${vendor.bg} flex items-center justify-center mb-3`}>
                    <Icon className={`w-5 h-5 ${vendor.color}`} />
                  </div>
                  <p className="font-semibold text-gray-900 text-sm leading-tight">{vendor.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{vendor.category}</p>
                  <div className="flex items-center gap-1 mt-2 text-xs text-gray-400">
                    <MapPin className="w-3 h-3" />
                    {vendor.location}
                  </div>
                  <div className="mt-2 text-[10px] text-emerald-600 font-medium">Starting at {vendor.price}</div>
                </div>
              );
            })}
          </div>
        </section>

        <div className="border-t border-gray-100 my-8" />

        {/* Final CTA */}
        <section className="pb-10 text-center" data-testid="vendor-landing-final-cta">
          <p className="text-sm text-gray-600 mb-4">Start getting booked for real events in your area</p>
          <Button
            onClick={handleJoin}
            variant="outline"
            fullWidth="true"
            className="w-full h-auto min-h-[52px] rounded-full px-4 sm:px-8 py-3 sm:py-5 border-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-xs sm:text-sm font-semibold whitespace-normal text-center leading-snug"
            data-testid="vendor-landing-final-join-btn"
          >
            <span className="inline-flex items-center justify-center flex-wrap gap-x-2 gap-y-0">
              <span>Start getting booked — build your profile</span>
              <ArrowRight className="w-4 h-4 shrink-0" />
            </span>
          </Button>
        </section>
      </main>
      <Footer />
    </div>
  );
}
